/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { FormSubmissionDetailResponse } from '../models/form-submission-detail-response';

/**
 * Admin recovery when WMS workflow callbacks failed and clinical submission state is out of sync with WMS. Restarts the existing workflow instance on the active route without changing workflow_instance_id.
 */
@Injectable({ providedIn: 'root' })
export class ClinicalWorkflowRecoveryService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `restartClinicalSubmissionWorkflowSync()` */
  static readonly RestartClinicalSubmissionWorkflowSyncPath =
    '/api/v1/clinical/submissions/{submissionId}/restart-workflow-sync';

  /**
   * Restart WMS workflow sync for a submission (callback recovery).
   *
   * Repairs a WMS-integrated clinical submission when the WMS→clinical callback failed and states are misaligned (e.g. clinical SUBMITTED but WMS COMPLETED). Clears clinical approval/rejection history, keeps the same workflow_instance_id, rebinds the WMS instance to the active route workflow, and resets both sides to a fresh first-stage SUBMITTED/PENDING state. Requires RESTART_CLINICAL_WORKFLOW_SUBMISSION privilege.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `restartClinicalSubmissionWorkflowSync()` instead.
   *
   * This method doesn't expect any request body.
   */
  restartClinicalSubmissionWorkflowSync$Response(
    params: {
      submissionId: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<FormSubmissionDetailResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClinicalWorkflowRecoveryService.RestartClinicalSubmissionWorkflowSyncPath,
      'post'
    );
    if (params) {
      rb.path('submissionId', params.submissionId, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<FormSubmissionDetailResponse>;
        })
      );
  }

  /**
   * Restart WMS workflow sync for a submission (callback recovery).
   *
   * Repairs a WMS-integrated clinical submission when the WMS→clinical callback failed and states are misaligned (e.g. clinical SUBMITTED but WMS COMPLETED). Clears clinical approval/rejection history, keeps the same workflow_instance_id, rebinds the WMS instance to the active route workflow, and resets both sides to a fresh first-stage SUBMITTED/PENDING state. Requires RESTART_CLINICAL_WORKFLOW_SUBMISSION privilege.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `restartClinicalSubmissionWorkflowSync$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  restartClinicalSubmissionWorkflowSync(
    params: {
      submissionId: number;
    },
    context?: HttpContext
  ): Observable<FormSubmissionDetailResponse> {
    return this.restartClinicalSubmissionWorkflowSync$Response(
      params,
      context
    ).pipe(
      map(
        (
          r: StrictHttpResponse<FormSubmissionDetailResponse>
        ): FormSubmissionDetailResponse => r.body
      )
    );
  }
}
